import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark';

const THEME_STORAGE_KEY = 'worknest-theme-mode';

@Injectable({ providedIn: 'root' })
export class ThemeModeService {
  private readonly document = inject(DOCUMENT);

  readonly mode = signal<ThemeMode>(this.resolveInitialTheme());
  readonly isDarkMode = computed(() => this.mode() === 'dark');
  readonly themeModeKey = computed(() => (this.isDarkMode() ? 'COMMON.THEME.DARK' : 'COMMON.THEME.LIGHT'));
  readonly targetThemeModeKey = computed(() => (this.isDarkMode() ? 'COMMON.THEME.LIGHT' : 'COMMON.THEME.DARK'));
  readonly themeLabelKey = computed(() =>
    this.isDarkMode() ? 'COMMON.THEME.DARK_HINT' : 'COMMON.THEME.LIGHT_HINT',
  );
  readonly toggleLabelKey = computed(() =>
    this.isDarkMode() ? 'COMMON.THEME.SWITCH_TO_LIGHT' : 'COMMON.THEME.SWITCH_TO_DARK',
  );

  constructor() {
    effect(() => {
      const mode = this.mode();
      const root = this.document.documentElement;

      root.classList.toggle('wn-dark', mode === 'dark');
      root.style.colorScheme = mode;

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(THEME_STORAGE_KEY, mode);
      }
    });
  }

  toggleTheme(): void {
    this.mode.update((m) => (m === 'dark' ? 'light' : 'dark'));
  }

  private resolveInitialTheme(): ThemeMode {
    if (typeof localStorage !== 'undefined') {
      const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      if (storedTheme === 'light' || storedTheme === 'dark') {
        return storedTheme;
      }
    }

    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      return 'dark';
    }

    return 'light';
  }
}
